import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Textarea } from '@werkstatt/ui';
import {
  AlertTriangle, Archive, ArchiveRestore, CheckCheck, Clock, CornerUpLeft,
  Download, Eye, EyeOff, Link2Off, Loader2, Paperclip, RefreshCw, Send, Trash2, X,
} from 'lucide-react';
import api from '../../lib/api';
import { readApiError } from '../../lib/apiError';
import { MessageBody } from './MessageBody';
import { AssignBooking } from './AssignBooking';
import type { EmailSettings, MailMessage, ThreadDetail } from '../../types/mailbox';
import { logger } from '@/utils/logger';
import { useAuthStore } from '../../store/authStore';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Wird vorbereitet',
  queued: 'Wartet auf Versand',
  sent: 'Zugestellt',
  failed: 'Nicht zugestellt',
  received: 'Empfangen',
};

function formatDateTime(value?: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function DeliveryBadge({ message }: { message: MailMessage }) {
  if (message.direction === 'inbound') {
    return <Badge variant="secondary">Kundenantwort</Badge>;
  }
  if (message.delivery_status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <CheckCheck className="h-3.5 w-3.5" aria-hidden />
        {STATUS_LABEL.sent}
      </span>
    );
  }
  if (message.delivery_status === 'failed') {
    return <Badge variant="danger">{STATUS_LABEL.failed}</Badge>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Clock className="h-3.5 w-3.5" aria-hidden />
      {STATUS_LABEL[message.delivery_status] ?? message.delivery_status}
    </span>
  );
}

interface Props {
  threadId: number;
  onDeleted?: () => void;
}

// Muss zu MAX_ATTACHMENT_BYTES im Backend passen — hier nur, damit der Nutzer
// den Fehler vor dem Hochladen sieht statt danach.
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

interface PendingAttachment {
  filename: string;
  content_type: string;
  content_b64: string;
  size: number;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function Conversation({ threadId, onDeleted }: Props) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const [reply, setReply] = useState('');
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mailbox-thread', threadId],
    queryFn: async () =>
      (await api.get<ThreadDetail>(`/mailbox/threads/${threadId}`)).data,
  });

  const { data: emailSettings } = useQuery({
    queryKey: ['email-settings-signature'],
    queryFn: async () => (await api.get<EmailSettings>('/settings/email')).data,
    staleTime: 5 * 60_000,
  });
  const signature = emailSettings?.email_signature ?? '';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mailbox-thread', threadId] });
    queryClient.invalidateQueries({ queryKey: ['mailbox-threads'] });
    queryClient.invalidateQueries({ queryKey: ['mailbox-stats'] });
  };

  // Ungelesene Kundenantworten gelten beim Öffnen als gelesen.
  const unread = data?.thread.unread_count ?? 0;
  useEffect(() => {
    if (unread > 0) {
      api.post(`/mailbox/threads/${threadId}/read`).then(invalidate).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, unread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [data?.messages.length]);

  // Ein Anhang gehoert zu genau einem Vorgang — beim Wechsel wegwerfen. Die
  // Signatur steht schon als Vorschlag im Feld, lässt sich vor dem Senden
  // aber noch entfernen oder anpassen.
  useEffect(() => {
    setPending([]);
    setReply(signature ? `\n\n${signature}` : '');
    setConfirmingDelete(false);
  }, [threadId, signature]);

  const replyMutation = useMutation({
    mutationFn: async (text: string) =>
      (await api.post(`/mailbox/threads/${threadId}/reply`, {
        body_text: text,
        attachments: pending.map(({ size: _size, ...rest }) => rest),
      })).data,
    onSuccess: () => {
      toast.success('Antwort wird versendet');
      setReply('');
      setPending([]);
      invalidate();
    },
    onError: (error: unknown) => {
      logger.error('Reply failed', error);
      toast.error(readApiError(error, 'Antwort konnte nicht gesendet werden'));
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (messageId: number) =>
      (await api.post(`/mailbox/messages/${messageId}/retry`)).data,
    onSuccess: () => {
      toast.success('Erneuter Versand angestoßen');
      invalidate();
    },
    onError: (error: unknown) =>
      toast.error(readApiError(error, 'Erneuter Versand fehlgeschlagen')),
  });

  const closeMutation = useMutation({
    mutationFn: async (reopen: boolean) =>
      (await api.post(`/mailbox/threads/${threadId}/close?reopen=${reopen}`)).data,
    onSuccess: () => invalidate(),
    onError: (error: unknown) =>
      toast.error(readApiError(error, 'Status konnte nicht geändert werden')),
  });

  const hideMutation = useMutation({
    mutationFn: async (unhide: boolean) =>
      (await api.post(`/mailbox/threads/${threadId}/hide?unhide=${unhide}`)).data,
    onSuccess: (_result, unhide) => {
      toast.success(unhide ? 'Wieder eingeblendet' : 'Ausgeblendet');
      invalidate();
    },
    onError: (error: unknown) =>
      toast.error(readApiError(error, 'Status konnte nicht geändert werden')),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => api.delete(`/mailbox/threads/${threadId}`),
    onSuccess: () => {
      toast.success('Konversation gelöscht');
      invalidate();
      onDeleted?.();
    },
    onError: (error: unknown) =>
      toast.error(readApiError(error, 'Löschen fehlgeschlagen')),
  });

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;

    const accepted: PendingAttachment[] = [];
    for (const file of Array.from(files)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`„${file.name}" ist größer als 5 MB.`);
        continue;
      }
      accepted.push({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        content_b64: await readAsBase64(file),
        size: file.size,
      });
    }

    setPending((current) => [...current, ...accepted].slice(0, 5));
    if (fileRef.current) fileRef.current.value = '';
  };

  const downloadAttachment = async (messageId: number,
                                    attachment: { filename: string; index: number }) => {
    try {
      const response = await api.get(
        `/mailbox/messages/${messageId}/attachments/${attachment.index}`,
        { responseType: 'blob' },
      );
      // Der Download braucht den Authorization-Header, ein einfaches <a href>
      // wuerde ihn nicht mitschicken.
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Attachment download failed', error);
      toast.error(readApiError(error, 'Anhang konnte nicht geladen werden'));
    }
  };

  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
      </div>
    );
  }

  const { thread, messages, booking } = data;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div className="min-w-0">
          <h2 className="font-display truncate text-base font-semibold">{thread.subject}</h2>
          <p className="truncate text-sm text-muted-foreground">
            {thread.customer_name ? `${thread.customer_name} · ` : ''}
            {thread.customer_email}
          </p>
          {booking ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {booking.local_datetime}
              {booking.vehicle_name ? ` · ${booking.vehicle_name}` : ''}
              {booking.category ? ` (${booking.category})` : ''}
            </p>
          ) : (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Link2Off className="h-3.5 w-3.5" aria-hidden />
              Keiner Buchung zugeordnet
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {thread.reference_tag && <Badge variant="outline">{thread.reference_tag}</Badge>}
          <AssignBooking threadId={thread.id} assigned={booking} />
          <Button
            size="sm"
            variant="outline"
            onClick={() => closeMutation.mutate(thread.is_closed)}
          >
            {thread.is_closed ? (
              <><ArchiveRestore className="mr-1.5 h-4 w-4" aria-hidden />Wieder öffnen</>
            ) : (
              <><Archive className="mr-1.5 h-4 w-4" aria-hidden />Abschließen</>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => hideMutation.mutate(thread.is_hidden)}
            disabled={hideMutation.isPending}
            title={thread.is_hidden ? 'Wieder einblenden' : 'Für den Betrieb irrelevante Post ausblenden'}
          >
            {thread.is_hidden ? (
              <><Eye className="mr-1.5 h-4 w-4" aria-hidden />Einblenden</>
            ) : (
              <><EyeOff className="mr-1.5 h-4 w-4" aria-hidden />Ausblenden</>
            )}
          </Button>
          {isAdmin && (
            confirmingDelete ? (
              <>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
                  Endgültig löschen
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(false)}>
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(true)}>
                <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
                Löschen
              </Button>
            )
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {messages.map((message) => (
          <article
            key={message.id}
            className={`rounded-lg border px-4 py-3 ${
              message.direction === 'inbound'
                ? 'border-border bg-muted/40'
                : 'border-primary/30 bg-primary/5'
            }`}
          >
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0 text-sm">
                <span className="font-medium">
                  {message.direction === 'inbound'
                    ? (thread.customer_name || thread.customer_email)
                    : (message.from_name || 'Vermietung')}
                </span>
                <span className="text-muted-foreground">
                  {' → '}{message.to_emails.join(', ')}
                </span>
                {message.cc_emails.length > 0 && (
                  <span className="text-muted-foreground">
                    {' · CC: '}{message.cc_emails.join(', ')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <DeliveryBadge message={message} />
                <time className="text-xs text-muted-foreground">
                  {formatDateTime(message.sent_at || message.created_at || '')}
                </time>
              </div>
            </div>

            <p className="mb-2 text-sm font-medium">{message.subject}</p>

            {message.delivery_status === 'failed' && (
              <div
                role="alert"
                className="mb-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <div className="flex-1">
                  <p>{message.error_message || 'Der Versand ist fehlgeschlagen.'}</p>
                  <p className="text-xs text-muted-foreground">
                    {message.attempts} Versuch(e)
                  </p>
                </div>
                {message.can_retry && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => retryMutation.mutate(message.id)}
                    disabled={retryMutation.isPending}
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Erneut senden
                  </Button>
                )}
              </div>
            )}

            <MessageBody
              html={message.body_html}
              text={message.body_text}
              untrusted={message.direction === 'inbound'}
            />

            {message.attachments.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2">
                {message.attachments.map((attachment) => (
                  <li key={`${message.id}-${attachment.index}`}>
                    <button
                      type="button"
                      onClick={() => downloadAttachment(message.id, attachment)}
                      className="inline-flex items-center gap-1.5 rounded-md border
                        border-border px-2 py-1 text-xs text-muted-foreground
                        hover:bg-muted focus-visible:outline-none focus-visible:ring-2
                        focus-visible:ring-ring"
                    >
                      <Paperclip className="h-3.5 w-3.5" aria-hidden />
                      {attachment.filename}
                      <span>({Math.max(1, Math.round(attachment.size_bytes / 1024))} kB)</span>
                      <Download className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
        <div ref={endRef} />
      </div>

      <footer className="flex-none border-t border-border px-5 py-4">
        <label htmlFor="reply-body" className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
          <CornerUpLeft className="h-4 w-4" aria-hidden />
          Antwort an {thread.customer_email}
        </label>
        <Textarea
          id="reply-body"
          value={reply}
          onChange={(event) => setReply(event.target.value)}
          rows={4}
          placeholder="Ihre Antwort an den Kunden …"
        />
        {pending.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-2">
            {pending.map((item, index) => (
              <li
                key={`${item.filename}-${index}`}
                className="inline-flex items-center gap-1.5 rounded-md border
                  border-border px-2 py-1 text-xs"
              >
                <Paperclip className="h-3.5 w-3.5" aria-hidden />
                {item.filename}
                <span className="text-muted-foreground">
                  ({Math.max(1, Math.round(item.size / 1024))} kB)
                </span>
                <button
                  type="button"
                  onClick={() => setPending((current) =>
                    current.filter((_, position) => position !== index))}
                  aria-label={`${item.filename} entfernen`}
                  className="rounded hover:text-destructive focus-visible:outline-none
                    focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}

        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => addFiles(event.target.files)}
        />

        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Die Antwort wird im Layout Ihrer Vermietung versendet und bleibt hier
            im Verlauf.
          </p>
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={pending.length >= 5}
          >
            <Paperclip className="mr-1.5 h-4 w-4" aria-hidden />
            Anhang
          </Button>
          <Button
            onClick={() => replyMutation.mutate(reply)}
            disabled={!reply.trim() || replyMutation.isPending}
          >
            {replyMutation.isPending
              ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
              : <Send className="mr-1.5 h-4 w-4" aria-hidden />}
            Senden
          </Button>
        </div>
      </footer>
    </div>
  );
}
